import { useEffect, useRef, useState } from "react";
import { CancellableAPIRequest } from "restAPI";

export type EntityNavigatorParams = {
  api: (payload: any) => CancellableAPIRequest<any>;
  payload: any;
  defaultPage: number;
  totalEntities: number;
  onEntityChange: (id: number | string, page: number) => void;
  onPrefetchNext?: (entityId: number | string) => void;
};

export default function useEntityNavigator({
  api,
  payload,
  defaultPage,
  totalEntities,
  onEntityChange,
  onPrefetchNext,
}: EntityNavigatorParams) {
  const [page, setPage] = useState(defaultPage);
  const cancelRef = useRef<() => void>();
  const prefetchCancelRef = useRef<() => void>();

  // Fetch the next entity to get its ID and trigger onPrefetchNext.
  const fetchNextEntity = (currentPage: number) => {
    if (currentPage + 1 >= totalEntities || !onPrefetchNext) return;

    if (prefetchCancelRef.current) {
      prefetchCancelRef.current();
    }

    const { cancel, promise } = api({
      ...payload,
      page: currentPage + 1,
      size: 1,
    });
    prefetchCancelRef.current = cancel;

    promise
      .then((r) => {
        const content = r?.content ?? r?.data?.content;
        const nextEntity = content?.[0];
        if (nextEntity?.id) {
          onPrefetchNext(nextEntity.id);
        }
      })
      .catch(() => {});
  };

  // On initial mount, fetch the next entity to trigger onPrefetchNext
  // so the first "next" navigation has a cached AI summary.
  useEffect(() => {
    fetchNextEntity(defaultPage);
  }, []);

  const gotTo = async (page: number) => {
    setPage(page);
    if (cancelRef.current) {
      cancelRef.current();
    }
    try {
      const { cancel, promise } = api({ ...payload, page, size: 1 });
      cancelRef.current = cancel;
      return promise.then((r) => {
        const content = r?.content ?? r?.data?.content;
        const entity = content?.[0];
        if (entity?.id) {
          onEntityChange(entity.id, page);
          fetchNextEntity(page);
        }
      });
    } catch (e) {
      console.error(e);
    }
  };

  return {
    hasNext: page < totalEntities - 1,
    hasPrev: page > 0,
    next: () => gotTo(page + 1),
    prev: () => gotTo(page - 1),
  };
}
